import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { buildFeedFilters, dedupeEvents, filterFeed, nextUntil, sortEvents } from "@/nostr/api/feed"
import type { NostrEvent } from "@/nostr/types"
import { useNostr } from "@/state/NostrContext"
import { useSettings } from "@/state/SettingsContext"
import { uniqueRelayUrls } from "@/utils/url"

export interface TimelineOptions {
  relays: string[]
  authors?: string[]
  kinds?: number[]
  search?: string
  limit?: number
  enabled?: boolean
  /** Overrides the user setting when provided. */
  includeReplies?: boolean
}

export interface TimelineResult {
  events: NostrEvent[]
  loading: boolean
  loadingMore: boolean
  hasMore: boolean
  eose: boolean
  closeReasons: string[]
  loadMore: () => void
  refresh: () => void
}

/**
 * Relay-first timeline.
 *
 * Subscribes to exactly the relays it is given (one relay, a relay set or the
 * user's relays), de-duplicates events arriving from several relays and
 * paginates with an `until` cursor. The subscription is closed whenever the
 * relay set, the filter or the component changes.
 */
export function useTimeline(options: TimelineOptions): TimelineResult {
  const { pool } = useNostr()
  const { settings } = useSettings()
  const relays = useMemo(() => uniqueRelayUrls(options.relays), [options.relays.join(",")])
  const enabled = options.enabled !== false && relays.length > 0
  const limit = options.limit ?? 40
  const search = options.search?.trim() || undefined

  const [events, setEvents] = useState<NostrEvent[]>([])
  const [loading, setLoading] = useState(enabled)
  const [loadingMore, setLoadingMore] = useState(false)
  const [hasMore, setHasMore] = useState(true)
  const [eose, setEose] = useState(false)
  const [closeReasons, setCloseReasons] = useState<string[]>([])
  const eventsRef = useRef<NostrEvent[]>([])
  const subscriptionRef = useRef<{ close: () => void } | undefined>(undefined)
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const pending = useRef<NostrEvent[]>([])

  const authorsKey = (options.authors ?? []).join(",")
  const kindsKey = (options.kinds ?? []).join(",")
  const queryKey = `${relays.join("|")}#${authorsKey}#${kindsKey}#${search ?? ""}#${limit}`

  const updateEvents = useCallback((incoming: NostrEvent[], replace: boolean) => {
    const next = sortEvents(dedupeEvents(replace ? incoming : [...eventsRef.current, ...incoming]))
    eventsRef.current = next
    setEvents(next)
  }, [])

  const flush = useCallback(() => {
    if (flushTimer.current) {
      clearTimeout(flushTimer.current)
      flushTimer.current = undefined
    }
    if (pending.current.length === 0) return
    const batch = pending.current
    pending.current = []
    updateEvents(batch, false)
  }, [updateEvents])

  const start = useCallback(
    (mode: "reset" | "more") => {
      subscriptionRef.current?.close()
      if (!enabled) {
        setLoading(false)
        setLoadingMore(false)
        return
      }

      const until = mode === "more" ? nextUntil(eventsRef.current) : undefined
      if (mode === "more" && until === undefined) {
        setHasMore(false)
        return
      }

      const filters = buildFeedFilters({
        relayUrls: relays,
        authors: options.authors,
        limit,
        until,
        search,
      })
      if (options.kinds && options.kinds.length > 0) {
        for (const filter of filters) filter.kinds = options.kinds
      }

      if (mode === "reset") {
        pending.current = []
        eventsRef.current = []
        setEvents([])
        setEose(false)
        setHasMore(true)
        setCloseReasons([])
        setLoading(true)
      } else {
        setLoadingMore(true)
      }

      let received = 0
      const reasons: string[] = []

      subscriptionRef.current = pool.subscribe(filters, {
        relays,
        onEvent: (event) => {
          received += 1
          pending.current.push(event)
          if (!flushTimer.current) flushTimer.current = setTimeout(flush, 150)
        },
        onEose: () => {
          flush()
          setLoading(false)
          setLoadingMore(false)
          setEose(true)
          if (received === 0) setHasMore(false)
          else if (received < limit * 0.25) setHasMore(false)
          if (reasons.length > 0) setCloseReasons([...new Set(reasons)])
        },
        onClosed: (message, relay) => {
          reasons.push(`${relay}: ${message}`)
        },
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pool, enabled, queryKey, limit, search, flush],
  )

  useEffect(() => {
    start("reset")
    return () => {
      subscriptionRef.current?.close()
      subscriptionRef.current = undefined
      if (flushTimer.current) clearTimeout(flushTimer.current)
      flushTimer.current = undefined
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryKey, enabled, pool])

  const loadMore = useCallback(() => {
    if (loading || loadingMore || !hasMore) return
    start("more")
  }, [loading, loadingMore, hasMore, start])

  const refresh = useCallback(() => start("reset"), [start])

  const includeReplies = options.includeReplies ?? settings.includeReplies
  const visibleEvents = useMemo(
    () => filterFeed(events, { includeReplies, mutedWords: settings.mutedWords }),
    [events, includeReplies, settings.mutedWords],
  )

  return {
    events: visibleEvents,
    loading,
    loadingMore,
    hasMore,
    eose,
    closeReasons,
    loadMore,
    refresh,
  }
}
